import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { getJobs, createJob, getClients } from '../services/api';
import { Briefcase, Plus, Search, MapPin, X, Users } from 'lucide-react';
import toast from 'react-hot-toast';

const statusColor = (s) =>
  s === 'open'        ? 'bg-green-50 text-green-700' :
  s === 'in_progress' ? 'bg-blue-50 text-blue-700' :
  s === 'fulfilled'   ? 'bg-gray-50 text-gray-500' :
  s === 'cancelled'   ? 'bg-red-50 text-red-600' :
  'bg-amber-50 text-amber-700';

const priorityColor = (p) =>
  p === 'urgent' ? 'bg-red-50 text-red-600' :
  p === 'high'   ? 'bg-orange-50 text-orange-600' :
  p === 'normal' ? 'bg-blue-50 text-blue-600' :
  'bg-gray-50 text-gray-500';

const AddJobModal = ({ onClose }) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    title: '', client_id: '', worker_category: 'blue_collar',
    employment_type: 'contractual', positions: 1,
    location: '', min_experience: 0, max_experience: 5,
    priority: 'normal', job_description: '',
    bill_rate: '', pay_rate: ''
  });

  const { data: clientsData } = useQuery({
    queryKey: ['clients-list'],
    queryFn : () => getClients({ limit: 100 }).then(r => r.data)
  });

  const mutation = useMutation({
    mutationFn: createJob,
    onSuccess: () => {
      toast.success('Job requisition created!');
      queryClient.invalidateQueries(['jobs']);
      onClose();
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to create job')
  });

  const handle = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    mutation.mutate({
      ...form,
      positions      : parseInt(form.positions),
      min_experience : parseFloat(form.min_experience),
      max_experience : parseFloat(form.max_experience),
      bill_rate      : form.bill_rate ? parseFloat(form.bill_rate) : undefined,
      pay_rate       : form.pay_rate  ? parseFloat(form.pay_rate)  : undefined
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6 max-h-screen overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-semibold text-gray-900">New Job Requisition</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Job Title *</label>
            <input name="title" value={form.title} onChange={handle} required
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g. Machine Operator" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Client *</label>
            <select name="client_id" value={form.client_id} onChange={handle} required
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="">Select Client</option>
              {clientsData?.data?.map(c => (
                <option key={c.id} value={c.id}>{c.company_name}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Worker Category *</label>
              <select name="worker_category" value={form.worker_category} onChange={handle}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="blue_collar">Blue Collar</option>
                <option value="white_collar">White Collar</option>
                <option value="specialized">Specialized</option>
                <option value="technical">Technical</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Employment Type *</label>
              <select name="employment_type" value={form.employment_type} onChange={handle}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="contractual">Contractual</option>
                <option value="permanent">Permanent</option>
                <option value="temporary">Temporary</option>
                <option value="part_time">Part Time</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">No. of Positions *</label>
              <input name="positions" type="number" value={form.positions} onChange={handle} min="1"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
              <select name="priority" value={form.priority} onChange={handle}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
            <input name="location" value={form.location} onChange={handle}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g. Chennai Plant" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Pay Rate (₹/month)</label>
              <input name="pay_rate" type="number" value={form.pay_rate} onChange={handle}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g. 15000" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Bill Rate (₹/month)</label>
              <input name="bill_rate" type="number" value={form.bill_rate} onChange={handle}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g. 18000" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Job Description</label>
            <textarea name="job_description" value={form.job_description} onChange={handle} rows={3}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Describe the job requirements..." />
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={mutation.isPending}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
              {mutation.isPending ? 'Saving...' : 'Create Job'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Jobs = () => {
  const navigate = useNavigate();
  const [search,  setSearch]  = useState('');
  const [status,  setStatus]  = useState('');
  const [showAdd, setShowAdd] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['jobs', search, status],
    queryFn : () => getJobs({ search, status, limit: 20 }).then(r => r.data)
  });

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Job Requisitions</h1>
          <p className="text-sm text-gray-400 mt-0.5">Manage manpower requirements from clients</p>
        </div>
        <button onClick={() => setShowAdd(true)}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700">
          <Plus size={16} />
          New Requisition
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by title or requisition number..."
            className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <select value={status} onChange={e => setStatus(e.target.value)}
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="">All Status</option>
          <option value="open">Open</option>
          <option value="in_progress">In Progress</option>
          <option value="fulfilled">Fulfilled</option>
          <option value="cancelled">Cancelled</option>
          <option value="on_hold">On Hold</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        {isLoading ? (
          <div className="text-center py-12 text-gray-400">Loading jobs...</div>
        ) : data?.data?.length === 0 ? (
          <div className="text-center py-12">
            <Briefcase size={32} className="mx-auto text-gray-200 mb-3" />
            <p className="text-gray-400 text-sm">No requisitions yet. Create your first one!</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="text-left text-xs font-medium text-gray-400 px-5 py-3">Job</th>
                <th className="text-left text-xs font-medium text-gray-400 px-5 py-3">Client</th>
                <th className="text-left text-xs font-medium text-gray-400 px-5 py-3">Location</th>
                <th className="text-left text-xs font-medium text-gray-400 px-5 py-3">Positions</th>
                <th className="text-left text-xs font-medium text-gray-400 px-5 py-3">Priority</th>
                <th className="text-left text-xs font-medium text-gray-400 px-5 py-3">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {data?.data?.map(job => (
                <tr key={job.id} onClick={() => navigate(`/jobs/${job.id}`)}
                  className="hover:bg-gray-50 transition cursor-pointer">
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-amber-50 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Briefcase size={14} className="text-amber-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{job.title}</p>
                        <p className="text-xs text-gray-400">{job.requisition_no}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-4 text-sm text-gray-600">
                    {job.clients?.company_name}
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-1 text-sm text-gray-600">
                      <MapPin size={13} className="text-gray-400" />
                      {job.location || '—'}
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-1 text-sm text-gray-600">
                      <Users size={13} className="text-gray-400" />
                      {job.filled_positions}/{job.positions}
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${priorityColor(job.priority)}`}>
                      {job.priority}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${statusColor(job.status)}`}>
                      {job.status?.replace('_', ' ')}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showAdd && <AddJobModal onClose={() => setShowAdd(false)} />}
    </div>
  );
};

export default Jobs;